import Typography from '@mui/material/Typography';
import { OrderState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef } from 'react';
import { Trans } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import {
  ALL_ORDERS_TAB_ORDER_STATES,
  DispatchURLParams,
  EXTERNAL_TAB_ORDER_STATES,
  ORDERS_TAB_ORDER_STATES,
} from '~constants/dispatch';
import { useStores } from '~store';
import OrdersDispatchStore from '~store/OrdersDispatchStore';

import StatusTabs, { StatusTab } from '../Tabs/StatusTabs';

export enum StatusTabValue {
  ORDERS = 'orders',
  PENDING = 'pending',
  ALL = 'all',
}

export const DEFAULT_TAB = StatusTabValue.ORDERS;

export const orderStatesByStatusTab: Record<StatusTabValue, OrderState[]> = {
  [StatusTabValue.ORDERS]: ORDERS_TAB_ORDER_STATES,
  [StatusTabValue.PENDING]: EXTERNAL_TAB_ORDER_STATES,
  [StatusTabValue.ALL]: [],
};

const allOrderStatesByStatusTab: Record<StatusTabValue, OrderState[]> = {
  [StatusTabValue.ORDERS]: ALL_ORDERS_TAB_ORDER_STATES,
  [StatusTabValue.PENDING]: EXTERNAL_TAB_ORDER_STATES,
  [StatusTabValue.ALL]: [],
};

// Drives the dispatch index's filter[external]: the External tab requests the received
// orders union (true); the Orders tab excludes received-pending-only orders (false); the
// All tab applies no External filtering (undefined).
const externalByStatusTab: Record<StatusTabValue, boolean | undefined> = {
  [StatusTabValue.PENDING]: true,
  [StatusTabValue.ORDERS]: false,
  [StatusTabValue.ALL]: undefined,
};

const statusTabs: StatusTab<StatusTabValue>[] = [
  { label: t('dispatch.tabs.external'), value: StatusTabValue.PENDING },
  { label: t('dispatch.tabs.orders'), value: StatusTabValue.ORDERS },
];

const getOrdersCount = (store: OrdersDispatchStore) => {
  return statusTabs.map((tab) => {
    if (tab.value === StatusTabValue.PENDING) {
      const count = store.ordersCount?.ordersExternal;

      if (count) {
        const badgeProps: (typeof tab)['badgeProps'] = {
          content: count.toString(),
          sx: (theme) => ({
            bgcolor: theme.brandV2.colors.treadYellow,
            // treadBlack flips to near-white in dark mode; textOnBrand is black in both.
            color: theme.semantic.textOnBrand,
          }),
          tooltipProps: {
            placement: 'bottom-start',
            title: (
              <Typography sx={{ fontSize: '11px', maxWidth: '150px' }}>
                <Trans
                  i18nKey={'dispatch.dispatch_v2.status_tabs.external_tab_tooltip'}
                  components={{ bold: <b /> }}
                />
              </Typography>
            ),
          },
        };

        return { ...tab, badgeProps };
      }

      return tab;
    }

    // Orders tab count: all authorized orders, excluding pending_request (those live on the
    // External tab) and external-only received orders (purely received-pending work, which the
    // Orders tab now excludes). A mixed order keeps an accepted job so it is not external-only
    // and stays counted here.
    const totalCount = store.ordersCount?.ordersTotal ?? 0;
    const pendingCount = store.ordersCount?.ordersPendingRequest ?? 0;
    const externalOnlyCount = store.ordersCount?.ordersExternalOnly ?? 0;
    const count = Math.max(0, totalCount - pendingCount - externalOnlyCount);

    if (count) {
      const badgeProps: (typeof tab)['badgeProps'] = {
        content: count.toString(),
        sx: (theme) => ({ bgcolor: theme.brandV2.colors.treadGray8 }),
      };

      return { ...tab, badgeProps };
    }

    return tab;
  });
};

interface OrdersStatusFilterProps {
  storeName: 'ordersDispatchStore' | 'ordersAssignedStore';
  storageTabKey: string;
}

const OrdersStatusFilter = observer(
  ({ storeName, storageTabKey }: OrdersStatusFilterProps) => {
    const stores = useStores();
    const ordersStore = stores[storeName];
    const showCanceledRejectedOrdersSettingEnabled =
      stores.userStore.companySettings.isEnabled('show_canceled_rejected_orders');
    const statesByTab = showCanceledRejectedOrdersSettingEnabled
      ? allOrderStatesByStatusTab
      : orderStatesByStatusTab;

    const [searchParams, setSearchParams] = useSearchParams();

    const currentTab = searchParams.get(DispatchURLParams.TAB) as StatusTabValue;
    const isValidTab = Object.values(StatusTabValue).includes(currentTab);
    const tabsAndStoreAreInSync = useRef(true);
    const isUserChange = useRef(false);

    const handleSelectedTabChange = useCallback(
      (value: StatusTabValue) => {
        isUserChange.current = true;

        setSearchParams((params) => {
          if (params.get(DispatchURLParams.TAB) === value) {
            params.set(DispatchURLParams.TAB, StatusTabValue.ALL);
          } else {
            params.set(DispatchURLParams.TAB, value);
          }

          return params;
        });
      },
      [setSearchParams],
    );

    const tabs = 'ordersCount' in ordersStore ? getOrdersCount(ordersStore) : statusTabs;

    useEffect(() => {
      if (isValidTab) {
        localStorage.setItem(storageTabKey, currentTab);

        if (isUserChange.current) {
          isUserChange.current = false;
          ordersStore.setFilters(
            {
              external: externalByStatusTab[currentTab],
              orderStates: statesByTab[currentTab],
              refinedOrderStates: undefined,
            },
            true,
          );

          return;
        }

        // tabs and store were re-synced from orderStates, so orderStates already
        // match the tab. But `external` is a separate dimension the orderStates
        // inference doesn't cover — sync it here so dispatch queries don't keep a
        // stale filter[external] (wrong External-tab membership) until the next
        // manual tab switch.
        if (!tabsAndStoreAreInSync.current) {
          tabsAndStoreAreInSync.current = true;

          const nextExternal = externalByStatusTab[currentTab];
          const currentExternal =
            'external' in ordersStore.filters ? ordersStore.filters.external : undefined;
          if (currentExternal !== nextExternal) {
            ordersStore.setFilters({ external: nextExternal }, true);
          }

          return;
        }

        ordersStore.setFilters(
          {
            external: externalByStatusTab[currentTab],
            orderStates: statesByTab[currentTab],
            refinedOrderStates: undefined,
          },
          true,
        );
      }
    }, [isValidTab, currentTab, statesByTab]);

    // when the selected order states are changed directly from the store, we need to
    // re-sync the selected tab. to do this, we set the corresponding querystring
    // parameter but also clear the "tabsAndStoreAreInSync" flag, so to prevent this
    // change from being propagated to the store.
    useEffect(() => {
      const orderStatesByCurrentTab = statesByTab[currentTab];
      const orderStatesHaveDiverged =
        JSON.stringify(orderStatesByCurrentTab) !==
        JSON.stringify(ordersStore.filters.orderStates);

      if (orderStatesHaveDiverged) {
        const inferredSelectedTabFromOrderStatesInStore = [
          StatusTabValue.ORDERS,
          StatusTabValue.PENDING,
          StatusTabValue.ALL,
        ].find((tab) => {
          const statesList = statesByTab[tab];

          const allOrderStatesMatched = statesList.every((orderState) => {
            return (ordersStore.filters.orderStates ?? []).includes(orderState);
          });

          return allOrderStatesMatched;
        });

        if (inferredSelectedTabFromOrderStatesInStore) {
          tabsAndStoreAreInSync.current = false;

          setSearchParams((params) => {
            params.set(DispatchURLParams.TAB, inferredSelectedTabFromOrderStatesInStore);

            return params;
          });
        }
      }
    }, [currentTab, statesByTab, JSON.stringify(ordersStore.filters.orderStates)]);

    if (!isValidTab) {
      return null;
    }

    return (
      <StatusTabs
        onChange={handleSelectedTabChange}
        selectedTab={currentTab}
        tabs={tabs}
      />
    );
  },
);

export default OrdersStatusFilter;
